import {
  AcceptInvitationRequest,
  ChangePasswordRequest,
  CurrentUser,
  dataResponse,
  ForgotPasswordRequest,
  InvitationPreview,
  LoginRequest,
  LoginResponse,
  MfaDisableRequest,
  MfaEnableRequest,
  MfaSetupResponse,
  MfaVerifyRequest,
  ResetPasswordRequest,
  SessionSummary,
} from '@horevia/contracts';
import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';
import { ApiRequestError, apiRequest, setCsrfToken } from '@/lib/api';

export const authKeys = {
  me: ['auth', 'me'] as const,
  sessions: ['auth', 'sessions'] as const,
};

const MeResponse = dataResponse(CurrentUser);
const LoginEnvelope = dataResponse(LoginResponse);
const SessionsResponse = dataResponse(z.array(SessionSummary));
const MfaSetupEnvelope = dataResponse(MfaSetupResponse);
const InvitationEnvelope = dataResponse(InvitationPreview);

/**
 * Session courante. Renvoie `null` (et non une erreur) quand l'utilisateur
 * n'est pas authentifié : le routeur s'en sert pour ses gardes.
 */
export const meQueryOptions = () =>
  queryOptions({
    queryKey: authKeys.me,
    staleTime: 60_000,
    retry: false,
    queryFn: async (): Promise<CurrentUser | null> => {
      try {
        const response = await apiRequest('/auth/me', {
          schema: MeResponse,
          skipAuthRedirect: true,
        });
        return response.data;
      } catch (error) {
        if (error instanceof ApiRequestError && error.status === 401) return null;
        throw error;
      }
    },
  });

export const sessionsQueryOptions = () =>
  queryOptions({
    queryKey: authKeys.sessions,
    queryFn: async () => (await apiRequest('/auth/sessions', { schema: SessionsResponse })).data,
  });

export async function login(payload: z.infer<typeof LoginRequest>) {
  const response = await apiRequest('/auth/login', {
    method: 'POST',
    body: payload,
    schema: LoginEnvelope,
  });
  if (response.data.status === 'AUTHENTICATED') setCsrfToken(response.data.csrfToken);
  return response.data;
}

export async function verifyMfa(payload: z.infer<typeof MfaVerifyRequest>) {
  const response = await apiRequest('/auth/mfa/verify', {
    method: 'POST',
    body: payload,
    schema: LoginEnvelope,
  });
  if (response.data.status === 'AUTHENTICATED') setCsrfToken(response.data.csrfToken);
  return response.data;
}

export async function logout(): Promise<void> {
  await apiRequest('/auth/logout', { method: 'POST' });
  setCsrfToken(undefined);
}

export async function forgotPassword(
  payload: z.infer<typeof ForgotPasswordRequest>,
): Promise<void> {
  await apiRequest('/auth/password/forgot', { method: 'POST', body: payload });
}

export async function resetPassword(payload: z.infer<typeof ResetPasswordRequest>): Promise<void> {
  await apiRequest('/auth/password/reset', { method: 'POST', body: payload });
}

export async function changePassword(
  payload: z.infer<typeof ChangePasswordRequest>,
): Promise<void> {
  await apiRequest('/auth/password/change', { method: 'POST', body: payload });
}

export async function acceptInvitation(
  payload: z.infer<typeof AcceptInvitationRequest>,
): Promise<void> {
  await apiRequest('/auth/invitations/accept', { method: 'POST', body: payload });
}

/** Détail de l'invitation (tenant, rôle proposé) affiché avant activation. */
export const invitationQueryOptions = (token: string) =>
  queryOptions({
    queryKey: ['auth', 'invitation', token] as const,
    retry: false,
    queryFn: async () =>
      (
        await apiRequest(`/auth/invitations/${encodeURIComponent(token)}`, {
          schema: InvitationEnvelope,
          skipAuthRedirect: true,
        })
      ).data,
  });

export async function startMfaSetup() {
  return (await apiRequest('/auth/mfa/setup', { method: 'POST', schema: MfaSetupEnvelope })).data;
}

export async function enableMfa(payload: z.infer<typeof MfaEnableRequest>): Promise<void> {
  await apiRequest('/auth/mfa/enable', { method: 'POST', body: payload });
}

export async function disableMfa(payload: z.infer<typeof MfaDisableRequest>): Promise<void> {
  await apiRequest('/auth/mfa/disable', { method: 'POST', body: payload });
}

export async function revokeSession(sessionId: string): Promise<void> {
  await apiRequest(`/auth/sessions/${sessionId}`, { method: 'DELETE' });
}

/**
 * Déconnecte les *autres* sessions de l'utilisateur.
 *
 * L'API n'expose pas de `DELETE /auth/sessions` : elle offre
 * `DELETE /auth/sessions/:id` (une session) et `POST /auth/logout-all` (toutes,
 * session courante comprise). Pour préserver la session en cours on révoque
 * chaque session non courante individuellement.
 */
export async function revokeOtherSessions(): Promise<number> {
  const { data } = await apiRequest('/auth/sessions', { schema: SessionsResponse });
  const others = data.filter((session) => !session.current);
  await Promise.all(others.map((session) => revokeSession(session.id)));
  return others.length;
}
